import { AnimatePresence, motion } from "framer-motion"
import { createPortal } from "react-dom"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"
import { formatAttendanceTypeCell, LOG_TABLE_EMPTY } from "./employeeDayLogTableCells"

const PAGE_SIZE = 50

const LOG_STATUS_ARROW = {
    ulaz: { color: "#16a34a", labelKey: "dayLogs.statusEntry", path: "M5 12h12M13 8l4 4-4 4" },
    izlaz: { color: "#dc2626", labelKey: "dayLogs.statusExit", path: "M19 12H7M11 8l-4 4 4 4" }
}

const LogStatusIcon = ({ status }) => {
    const { t } = useTranslation("stats")
    const cfg = LOG_STATUS_ARROW[status]
    if (!cfg) {
        const text = status && String(status).trim() ? status : "—"
        return <span>{text}</span>
    }
    const label = t(cfg.labelKey)
    return (
        <span className="asv-emp-day-log-status-icon" title={label} aria-label={label}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <path
                    d={cfg.path}
                    fill="none"
                    stroke={cfg.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
}

const formatDatum = (log) => {
    if (log?.datum && String(log.datum).trim()) return log.datum
    if (log?.dan && log?.mesec && log?.godina) {
        return `${String(log.dan).padStart(2, "0")}.${String(log.mesec).padStart(2, "0")}.${log.godina}`
    }
    return "—"
}

const formatCell = (value) => {
    if (value == null) return "—"
    const text = String(value).trim()
    return text || "—"
}

const formatIzlazMinutes = (log) => {
    if (log?.status !== "izlaz") return "—"
    const mins = log.min_izmedju_upisa
    if (mins == null || Number.isNaN(Number(mins))) return "—"
    return String(mins)
}

const getLogRowKey = (log, idx) =>
    log._id ? String(log._id) : `row-${idx}-${log.user_id}-${log.vreme}-${log.status}`

const formatCellExport = (value) => {
    if (value == null) return ""
    const text = String(value).trim()
    return text || ""
}

const logToExcelRow = (log, t) => {
    const attendanceType = formatAttendanceTypeCell(log, t)
    return [
        formatCellExport(log.ime),
        formatCellExport(log.prezime),
        formatDatum(log) === "—" ? "" : formatDatum(log),
        formatCellExport(log.vreme),
        formatCellExport(log.status),
        attendanceType === LOG_TABLE_EMPTY ? "" : attendanceType,
        formatCellExport(log.korisnikPotvrda),
        formatCellExport(log.izmenio),
        formatIzlazMinutes(log) === "—" ? "" : formatIzlazMinutes(log)
    ]
}

const flattenStatsMonthReport = (groupedData) => {
    if (!groupedData || typeof groupedData !== "object") return []

    const rows = []
    for (const logs of Object.values(groupedData)) {
        if (!Array.isArray(logs)) continue
        for (const log of logs) {
            rows.push(log)
        }
    }

    return rows
}

const AdminStatsMonthReportModal = () => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const isOpen = useAppStore((state) => state.statsMonthReportModalOpen)
    const loading = useAppStore((state) => state.loadingStatsMonthReport)
    const groupedData = useAppStore((state) => state.statsMonthReportData)
    const dateFilter = useAppStore((state) => state.statsMonthReportDateFilter)
    const closeStatsMonthReportModal = useAppStore((state) => state.closeStatsMonthReportModal)

    const modalContainerRef = useRef(null)
    const [page, setPage] = useState(1)

    const months = t("months", { returnObjects: true })
    const allRows = useMemo(() => flattenStatsMonthReport(groupedData), [groupedData])

    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE))

    useEffect(() => {
        setPage(1)
    }, [groupedData, dateFilter.month, dateFilter.year])

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages)
        }
    }, [page, totalPages])

    const pageRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE
        return allRows.slice(start, start + PAGE_SIZE)
    }, [allRows, page])

    const handleExportExcel = useCallback(() => {
        if (!allRows.length) return

        const headers = [
            t("monthReport.excelHeaders.firstName"),
            t("monthReport.excelHeaders.lastName"),
            t("monthReport.excelHeaders.date"),
            t("monthReport.excelHeaders.time"),
            t("monthReport.excelHeaders.status"),
            t("monthReport.excelHeaders.confirmationType"),
            t("monthReport.excelHeaders.confirmedBy"),
            t("monthReport.excelHeaders.editedBy"),
            t("monthReport.excelHeaders.exitMinutes")
        ]
        const rows = allRows.map((log) => logToExcelRow(log, t))
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, t("monthReport.excelSheetName"))

        const monthLabel = months[dateFilter.month]
        const fileName = t("monthReport.excelFileName", { month: monthLabel, year: dateFilter.year })
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName)
    }, [allRows, dateFilter.month, dateFilter.year, months, t])

    useLayoutEffect(() => {
        if (!isOpen) return undefined
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return
            }
            closeStatsMonthReportModal()
        }
        document.addEventListener("mousedown", handleClickOutside, true)
        return () => document.removeEventListener("mousedown", handleClickOutside, true)
    }, [isOpen, closeStatsMonthReportModal])

    const title = t("monthReport.title", {
        month: months[dateFilter.month],
        year: dateFilter.year
    })

    const recordCountLabel =
        allRows.length === 1
            ? t("monthReport.record_one", { count: allRows.length })
            : t("monthReport.record_other", { count: allRows.length })

    const modalContent = (
        <AnimatePresence>
            {isOpen ? (
                <motion.div
                    key="stats-month-report-modal"
                    className="asv-eLogs-root"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                >
                    <div
                        className={`asv-eLogs-backdrop ${isDarkTheme ? "asv-eLogs-backdrop--dark" : "asv-eLogs-backdrop--light"}`}
                        aria-hidden
                    />
                    <motion.div
                        ref={modalContainerRef}
                        className={`asv-month-report-modal ${isDarkTheme ? "asv-month-report-modal--dark" : "asv-month-report-modal--light"}`}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="asv-month-report-modal-inner">
                            <div className="absence-modal-close-buttons-container">
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={closeStatsMonthReportModal}
                                    aria-label={t("monthReport.closeAria")}
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            <div className={`asv-month-report-modal-header ${isDarkTheme ? "dark-color" : ""}`}>
                                <h2 className="asv-month-report-modal-title">{title}</h2>
                                {!loading && (
                                    <span className="asv-month-report-modal-meta">
                                        {recordCountLabel}
                                    </span>
                                )}
                            </div>

                            <div className="asv-month-report-modal-body">
                                {loading ? (
                                    <div className="asv-month-report-modal-loader">
                                        <LoadingSpinner />
                                    </div>
                                ) : allRows.length === 0 ? (
                                    <p className={`asv-month-report-empty ${isDarkTheme ? "dark-color" : ""}`}>
                                        {t("monthReport.empty")}
                                    </p>
                                ) : (
                                    <>
                                        <div className="asv-month-report-table-scroll">
                                            <table className={`asv-month-report-table ${isDarkTheme ? "asv-month-report-table--dark" : ""}`}>
                                                <thead>
                                                    <tr>
                                                        <th>{t("monthReport.excelHeaders.firstName")}</th>
                                                        <th>{t("monthReport.excelHeaders.lastName")}</th>
                                                        <th>{t("monthReport.columns.date")}</th>
                                                        <th>{t("dayLogs.columns.time")}</th>
                                                        <th className="asv-emp-day-logs-table-col-status" aria-label={t("dayLogs.columns.status")}>{t("dayLogs.columns.status")}</th>
                                                        <th>{t("monthReport.columns.confirmationType")}</th>
                                                        <th>{t("monthReport.columns.confirmedBy")}</th>
                                                        <th>{t("dayLogs.columns.editedBy")}</th>
                                                        <th>{t("monthReport.columns.exitMinutes")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pageRows.map((log, idx) => (
                                                        <tr key={getLogRowKey(log, idx)} className="asv-emp-day-log-row">
                                                            <td>{formatCell(log.ime)}</td>
                                                            <td>{formatCell(log.prezime)}</td>
                                                            <td>{formatDatum(log)}</td>
                                                            <td>{formatCell(log.vreme)}</td>
                                                            <td className="asv-emp-day-logs-table-col-status">
                                                                <LogStatusIcon status={log.status} />
                                                            </td>
                                                            <td>{formatAttendanceTypeCell(log, t)}</td>
                                                            <td>{formatCell(log.korisnikPotvrda)}</td>
                                                            <td>{formatCell(log.izmenio)}</td>
                                                            <td>{formatIzlazMinutes(log)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className={`asv-month-report-pagination ${isDarkTheme ? "asv-month-report-pagination--dark" : ""}`}>
                                            <button
                                                type="button"
                                                className="asv-month-report-page-btn"
                                                disabled={page <= 1}
                                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            >
                                                {t("monthReport.prev")}
                                            </button>
                                            <span className="asv-month-report-page-info">
                                                {t("monthReport.pageInfo", { page, total: totalPages })}
                                            </span>
                                            <button
                                                type="button"
                                                className="asv-month-report-page-btn"
                                                disabled={page >= totalPages}
                                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            >
                                                {t("monthReport.next")}
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            className={`kv-xlsx-button asv-month-report-export-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                            onClick={handleExportExcel}
                                        >
                                            {t("monthReport.exportExcel")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )

    return createPortal(modalContent, document.body)
}

export default AdminStatsMonthReportModal
